import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/app/Header";
import JobCard from "@/components/app/JobCard";
import JobDetailSheet from "@/components/app/JobDetailSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Lightning, BookmarkSimple, CheckCircle, Target, TrendUp,
  ArrowRight, Sparkle, UserCircle, GlobeHemisphereWest,
  ArrowClockwise, MagnifyingGlass, Warning, CreditCard,
  ChartBar, Briefcase, CalendarBlank,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Home() {
  const { user, profile, refreshProfile } = useAuth();
  const nav = useNavigate();

  const [stats, setStats] = useState(null);
  const [topMatches, setTopMatches] = useState([]);
  const [recentApplied, setRecentApplied] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchLoading, setMatchLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  // Profile readiness
  const profileReadiness = useMemo(() => {
    if (!profile) return 0;
    let s = 0;
    if (profile.full_name) s += 15;
    if (profile.skills?.length > 0) s += 25;
    if (profile.preferred_roles?.length > 0) s += 20;
    if (profile.experience_years) s += 15;
    if (profile.headline) s += 10;
    if (profile.preferred_locations?.length > 0) s += 10;
    if (profile.summary) s += 5;
    return Math.min(100, s);
  }, [profile]);

  useEffect(() => {
    (async () => {
      const data = await refreshProfile();
      if (!data?.profile) { nav("/onboarding"); return; }
      loadAll();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dashRes, savedRes, subRes] = await Promise.allSettled([
        api.get("/dashboard"),
        api.get("/jobs/saved"),
        api.get("/payments/status"),
      ]);

      if (dashRes.status === "fulfilled") {
        const d = dashRes.value.data;
        setStats(d.stats);
        setTopMatches(d.top_matches || []);
        setRecentApplied(d.recent_applied || []);
      }
      if (savedRes.status === "fulfilled") {
        setSavedJobs((savedRes.value.data.jobs || []).slice(0, 6));
      }
      if (subRes.status === "fulfilled") {
        setSubStatus(subRes.value.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }

    // Load recommended jobs
    setMatchLoading(true);
    try {
      const { data } = await api.get("/jobs/discover", {
        params: { limit: 6 },
      });
      setRecommended(data.jobs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setMatchLoading(false);
    }
  };

  const openJob = (job) => { setSelected(job); setSheetOpen(true); };
  const onAnalysed = (analysis) => {
    setRecommended((prev) => prev.map((j) => j.id === analysis.job_id ? { ...j, analysis } : j));
  };

  const firstName = profile?.full_name?.split(" ")[0] || user?.full_name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">

        {/* ── Welcome Hero ── */}
        <div className="mb-10 pb-8 border-b border-border">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="tick-label">{greeting}</span>
              <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                {firstName}.
              </h1>
              {profile?.headline && (
                <p className="mt-2 text-ink-soft text-sm max-w-lg">{profile.headline}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard">
                <Button className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-10 px-5">
                  <MagnifyingGlass size={13} weight="bold" className="mr-2" />
                  Find Jobs
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="outline" className="rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-10 px-4">
                  <UserCircle size={13} weight="bold" className="mr-2" />
                  Profile
                </Button>
              </Link>
            </div>
          </div>

          {/* Profile completeness bar */}
          {profileReadiness < 100 && (
            <div className="mt-6 border border-amber-200 bg-amber-50/60 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="tick-label text-amber-600">Profile {profileReadiness}% complete</span>
                <div className="mt-2 h-1.5 w-48 bg-amber-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-700 rounded-full"
                    style={{ width: `${profileReadiness}%` }}
                  />
                </div>
                <p className="mt-1.5 text-xs text-amber-700">
                  {profileReadiness < 50
                    ? "Add skills and target roles to unlock AI matching."
                    : "Add a few more details to improve your match scores."}
                </p>
              </div>
              <Link to="/profile">
                <Button variant="outline" className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-500 hover:text-white uppercase tracking-wider text-xs h-9 px-4">
                  Complete profile <ArrowRight size={11} weight="bold" className="ml-1.5" />
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {[
            {
              icon: Target, label: "Discovered", value: loading ? "—" : (stats?.discovered ?? 0),
              sub: "jobs in pipeline", color: "text-brand", bg: "bg-brand/10",
            },
            {
              icon: ChartBar, label: "Analysed", value: loading ? "—" : (stats?.analyzed ?? 0),
              sub: "AI reviews done", color: "text-violet-600", bg: "bg-violet-50",
            },
            {
              icon: BookmarkSimple, label: "Saved", value: loading ? "—" : (stats?.saved ?? 0),
              sub: "bookmarked", color: "text-emerald-600", bg: "bg-emerald-50",
            },
            {
              icon: CheckCircle, label: "Applied", value: loading ? "—" : (stats?.applied ?? 0),
              sub: "submitted", color: "text-blue-600", bg: "bg-blue-50",
            },
          ].map((s) => (
            <div key={s.label} className="border border-slate-200/60 bg-white shadow-sm p-4 hover:border-slate-300 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 ${s.bg} flex items-center justify-center`}>
                  <s.icon size={13} weight="bold" className={s.color} />
                </div>
                <span className="tick-label">{s.label}</span>
              </div>
              <div className={`font-mono font-black text-2xl ${s.color}`}>{s.value}</div>
              <div className="text-[11px] text-ink-mute mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Main Grid: Recommendations + Right Sidebar ── */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">

          {/* ── Left: Recommended Jobs ── */}
          <div className="xl:col-span-8">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <Lightning size={16} weight="fill" className="text-brand" />
                <span className="font-display font-bold text-lg tracking-tight">Recommended for you</span>
                <Badge variant="outline" className="border-brand text-brand text-[10px] uppercase tracking-wider px-2 py-0">
                  AI matched
                </Badge>
              </div>
              <Link to="/dashboard" className="text-xs text-ink-soft hover:text-brand font-semibold uppercase tracking-wider inline-flex items-center gap-1">
                See all <ArrowRight size={11} weight="bold" />
              </Link>
            </div>

            {matchLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="border border-slate-200/60 h-52 animate-pulse bg-slate-50/60 rounded" />
                ))}
              </div>
            ) : recommended.length === 0 ? (
              <div className="border border-slate-200/60 p-12 text-center">
                <Warning size={28} weight="duotone" className="mx-auto text-ink-mute mb-4" />
                <p className="font-display font-bold text-lg mb-2">No matches yet</p>
                <p className="text-sm text-ink-soft mb-5">Complete your profile so we can find the right jobs for you.</p>
                <Link to="/profile">
                  <Button className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-9 px-4">
                    Build your profile
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommended.map((j, i) => (
                  <div key={j.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                    <JobCard job={j} onOpen={openJob} onStateChange={loadAll} />
                  </div>
                ))}
              </div>
            )}

            {/* ── Recent Applied ── */}
            {recentApplied.length > 0 && (
              <div className="mt-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={15} weight="fill" className="text-blue-500" />
                    <span className="font-display font-bold text-base tracking-tight">Recently Applied</span>
                  </div>
                  <Link to="/applied" className="text-xs text-ink-soft hover:text-brand font-semibold uppercase tracking-wider inline-flex items-center gap-1">
                    View all <ArrowRight size={11} weight="bold" />
                  </Link>
                </div>
                <div className="divide-y divide-border border border-slate-200/60">
                  {recentApplied.map((item) => {
                    const j = item.job || item;
                    return (
                      <div key={item.job_id || j.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors">
                        <div className="min-w-0 flex-1">
                          <p className="font-display font-bold text-sm truncate">{j.title}</p>
                          <p className="text-xs text-ink-soft truncate">{j.company_name || j.company}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {item.match_score != null && (
                            <span className="font-mono font-black text-base text-brand">{item.match_score}</span>
                          )}
                          <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] uppercase tracking-wider">Applied</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar ── */}
          <div className="xl:col-span-4 space-y-6">

            {/* Subscription status */}
            <div className={`border p-5 ${subStatus?.active ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200/60 bg-white"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard size={14} weight="bold" className={subStatus?.active ? "text-emerald-600" : "text-ink-mute"} />
                  <span className="tick-label">{subStatus?.active ? "Pro Active" : "Free Plan"}</span>
                </div>
                {subStatus?.active && (
                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">Active</Badge>
                )}
              </div>
              {subStatus?.active ? (
                <>
                  <p className="font-display font-bold text-sm">{subStatus.plan_name}</p>
                  <p className="text-xs text-ink-soft mt-1">
                    {subStatus.amount_display}/month · renews {subStatus.expires_at ? new Date(subStatus.expires_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : "—"}
                  </p>
                  <Link to="/billing">
                    <Button variant="outline" className="mt-3 w-full rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs h-8">
                      Manage
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm text-ink-soft mt-1">Increase your chances of getting jobs 10x with unlimited AI drafts and premium sources.</p>
                  <Link to="/billing">
                    <Button className="mt-5 w-full rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-9">
                      Upgrade to Pro <ArrowRight size={11} weight="bold" className="ml-1" />
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Quick links */}
            <div className="border border-slate-200/60 bg-white p-5">
              <span className="tick-label mb-4 block">Quick Access</span>
              <div className="space-y-1">
                {[
                  { icon: MagnifyingGlass, label: "Discover Jobs", to: "/dashboard", color: "text-brand" },
                  { icon: BookmarkSimple, label: "Saved Jobs", to: "/saved", color: "text-emerald-600" },
                  { icon: CheckCircle, label: "Applied Jobs", to: "/applied", color: "text-blue-600" },
                  { icon: UserCircle, label: "My Profile", to: "/profile", color: "text-violet-600" },
                  { icon: GlobeHemisphereWest, label: "Settings", to: "/settings", color: "text-ink-soft" },
                ].map((l) => (
                  <Link key={l.to} to={l.to}>
                    <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors group">
                      <l.icon size={14} weight="bold" className={`${l.color} flex-shrink-0`} />
                      <span className="text-sm text-ink font-medium group-hover:text-brand transition-colors">{l.label}</span>
                      <ArrowRight size={11} weight="bold" className="ml-auto text-ink-mute group-hover:text-brand transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Saved jobs preview */}
            {savedJobs.length > 0 && (
              <div className="border border-slate-200/60 bg-white p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <BookmarkSimple size={14} weight="fill" className="text-emerald-600" />
                    <span className="tick-label">Saved</span>
                  </div>
                  <Link to="/saved" className="text-[11px] text-ink-soft hover:text-brand uppercase tracking-wider font-semibold">
                    View all
                  </Link>
                </div>
                <div className="space-y-3">
                  {savedJobs.slice(0, 4).map((item) => {
                    const j = item.job || item;
                    return (
                      <div key={item.job_id || j.id} className="flex items-start gap-2.5">
                        <div className="w-7 h-7 bg-slate-50 border border-slate-200/60 flex-shrink-0 flex items-center justify-center font-mono text-[10px] font-bold text-ink-mute">
                          {(j.company_name || "?")[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-ink truncate leading-tight">{j.title}</p>
                          <p className="text-[11px] text-ink-mute truncate">{j.company_name || j.company}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sources status */}
            <div className="border border-slate-200/60 bg-white p-5">
              <span className="tick-label mb-4 block">Marketplace Sources</span>
              <div className="space-y-2">
                {[
                  { name: "Remotive", active: true },
                  { name: "Remote OK", active: true },
                  { name: "We Work Remotely", active: true },
                  { name: "Arbeitnow", active: true },
                  { name: "Himalayas", active: true },
                  { name: "LinkedIn via JSearch", active: false, key: "RAPIDAPI_KEY" },
                  { name: "Indeed via JSearch", active: false, key: "RAPIDAPI_KEY" },
                  { name: "Glassdoor via JSearch", active: false, key: "RAPIDAPI_KEY" },
                  { name: "Adzuna", active: false, key: "ADZUNA_APP_KEY" },
                  { name: "Google Discovery", active: false, key: "SERPAPI_KEY" },
                  { name: "Naukri / Google Jobs", active: false, key: "SERPAPI_KEY" },
                ].map((src) => (
                  <div key={src.name} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-ink truncate">{src.name}</span>
                    {src.active ? (
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" title="Active" />
                    ) : (
                      <span className="flex-shrink-0 text-[9px] font-mono text-ink-mute bg-slate-100 px-1.5 py-0.5 border border-slate-200/60" title={`Needs ${src.key}`}>KEY</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <JobDetailSheet
        open={sheetOpen}
        job={selected}
        onOpenChange={setSheetOpen}
        onAnalysed={onAnalysed}
        onStateChange={loadAll}
      />
    </div>
  );
}
